// auth/AuthContext.js

import { createContext, useContext, useEffect, useState } from "react";
import {
  clearTokenRefreshState,
  restoreAuthenticatedSession,
  startTokenRefreshScheduler,
  stopTokenRefreshScheduler,
} from "../../services/authService";
import {
  clearActiveOrganization,
  getActiveOrganizationSelection,
  normalizeOrganizationSelection,
  setActiveOrganization,
} from "../shared/util";
import {
  clearLegacyDashboardBootstrapFlag,
  resetAuthSessionState,
} from "../shared/utils/authSessionState";
import { clearLoggedInUserRole } from "../shared/access";

const AuthContext = createContext();

const setStorageIfPresent = (key, value) => {
  if (value !== undefined && value !== null) {
    sessionStorage.setItem(key, String(value));
  }
};

const findOrganizationByTenantId = (organizations, tenantId) => {
  if (!tenantId) return undefined;

  return organizations.find((org) => {
    const selection = normalizeOrganizationSelection(org);
    return String(selection.tenantId) === String(tenantId);
  });
};

const hasPersistedSession = () => {
  try {
    return Boolean(
      sessionStorage.getItem("email") &&
      sessionStorage.getItem("mainTenantId") &&
      sessionStorage.getItem("_id"),
    );
  } catch {
    return false;
  }
};

const clearDashboardProfileBootstrap = () => {
  clearLegacyDashboardBootstrapFlag();
};

const persistSessionData = (response) => {
  const data = response?.data || {};
  const organizations = Array.isArray(data.organizations)
    ? data.organizations
    : [];
  const primaryOrg = organizations[0];
  const activeOrganizationSelection = getActiveOrganizationSelection();

  setStorageIfPresent("isSetupCompleted", data.isSetupCompleted);
  setStorageIfPresent("email", data.email);
  setStorageIfPresent("orgCode", data.orgCode ?? primaryOrg?.orgCode);
  setStorageIfPresent(
    "organization",
    data.organization ?? primaryOrg?.organization,
  );
  setStorageIfPresent("mainTenantId", data.tenantId);
  setStorageIfPresent("tenantId", data.tenantId);
  setStorageIfPresent("userName", data.userName);
  setStorageIfPresent("_id", data._id);

  if (!activeOrganizationSelection?.tenantId) return;

  const activeOrganization = findOrganizationByTenantId(
    organizations,
    activeOrganizationSelection.tenantId,
  );

  if (activeOrganization) {
    setActiveOrganization(activeOrganization);
  } else {
    clearActiveOrganization();
  }
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const initializeAuthSession = async () => {
      const shouldRestoreSession = hasPersistedSession();

      if (!shouldRestoreSession) {
        clearTokenRefreshState();
        clearLoggedInUserRole();

        if (isMounted) {
          setIsAuthenticated(false);
          setLoading(false);
        }
        return;
      }

      const isSessionRestored = await restoreAuthenticatedSession();

      if (!isMounted) {
        return;
      }

      setIsAuthenticated(isSessionRestored);

      if (isSessionRestored) {
        startTokenRefreshScheduler();
      } else {
        clearLoggedInUserRole();
      }

      setLoading(false);
    };

    void initializeAuthSession();

    return () => {
      isMounted = false;
      stopTokenRefreshScheduler();
    };
  }, []);

  const login = (data, { resetRefreshClock = true } = {}) => {
    console.log("Login data:", data);
    clearDashboardProfileBootstrap();
    persistSessionData(data);
    setIsAuthenticated(true);
    setLoading(false);
    startTokenRefreshScheduler({
      resetRefreshClock,
    });
  };

  const signup = (data) => {
    console.log("Sign Up data:", data);
    clearDashboardProfileBootstrap();
    persistSessionData(data);
    setIsAuthenticated(true);
    setLoading(false);
    startTokenRefreshScheduler({
      resetRefreshClock: true,
    });
  };

  const logout = () => {
    stopTokenRefreshScheduler();
    clearTokenRefreshState();
    resetAuthSessionState();
    clearLoggedInUserRole();

    setIsAuthenticated(false);
    setLoading(false);
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, login, logout, loading, signup }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
